/* ============================================================================
   PARHELIK — predictive search controller
   Vanilla, dependency-free, idempotent, debounced.

   For every [data-ph-search] root (inside a .ph-search-popup):
     • debounce input -> fetch /search/suggest?q=…&section_id=predictive-search
       (Shopify Section Rendering API) -> inject #shopify-section-predictive-search
       markup into [data-ph-search-results]
     • loading + clear states; popular-searches hidden once results show
     • keyboard nav: ArrowDown / ArrowUp move the active option, Enter follows it
       (or submits the form / opens "view all"), Escape closes the popup
     • open / close the parent .ph-search-popup (compatible with the header's own
       [data-ph-search-open] toggle — both just flip the .is-open class)

   HOOK CONTRACT (markup provides these):
     [data-ph-search]            root  (dataset: data-predictive, data-min-length)
     [data-ph-search-form]       GET form (action = routes.search_url)
     [data-ph-search-input]      <input name="q">
     [data-ph-search-results]    injection target
     [data-ph-search-option]     selectable row (carries data-ph-search-url)
     [data-ph-search-all]        "view all" link (Enter fallback)
     [data-ph-search-loading]    spinner (toggled .is-active)
     [data-ph-search-clear]      clear button (hidden attr)
     [data-ph-search-default]    popular links block
     [data-ph-search-close]      close trigger(s)
     [data-ph-search-open]       open trigger(s) anywhere in the document
   ========================================================================== */
(function () {
  'use strict';

  if (window.PHPredictiveSearch && window.PHPredictiveSearch._installed) return;

  var SEL = {
    root: '[data-ph-search]',
    popup: '.ph-search-popup',
    form: '[data-ph-search-form]',
    input: '[data-ph-search-input]',
    results: '[data-ph-search-results]',
    option: '[data-ph-search-option]',
    all: '[data-ph-search-all]',
    loading: '[data-ph-search-loading]',
    clear: '[data-ph-search-clear]',
    open: '[data-ph-search-open]',
    close: '[data-ph-search-close]'
  };

  var DEBOUNCE_MS = 280;
  var SECTION_ID = 'predictive-search';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function suggestBase() {
    var r =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.predictive_search_url) ||
      root().replace(/\/$/, '') + '/search/suggest';
    return r;
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* ---------- one controller per [data-ph-search] root ---------- */
  function PHSearch(rootEl) {
    this.root = rootEl;
    this.popup = rootEl.closest(SEL.popup);
    this.form = qs(SEL.form, rootEl);
    this.input = qs(SEL.input, rootEl);
    this.results = qs(SEL.results, rootEl);
    this.loadingEl = qs(SEL.loading, rootEl);
    this.clearEl = qs(SEL.clear, rootEl);

    this.predictive = rootEl.getAttribute('data-predictive') !== 'false';
    this.minLength = parseInt(rootEl.getAttribute('data-min-length'), 10) || 2;

    this.cache = {};
    this.activeIndex = -1;
    this.controller = null; // AbortController for the in-flight request

    if (!this.form || !this.input || !this.results) return;
    this.bind();
  }

  PHSearch.prototype.bind = function () {
    var self = this;

    if (this.predictive) {
      this.input.addEventListener('input', debounce(function () { self.onInput(); }, DEBOUNCE_MS));
    }

    // Keyboard navigation lives on the root so it covers input + results.
    this.root.addEventListener('keydown', function (e) { self.onKeydown(e); });

    // Submit guard: don't submit an empty query; if an option is active, follow it.
    this.form.addEventListener('submit', function (e) { self.onSubmit(e); });

    if (this.clearEl) {
      this.clearEl.addEventListener('click', function (e) {
        e.preventDefault();
        self.clearQuery();
      });
    }

    // Hover highlights an option (keeps keyboard + mouse state in sync).
    this.results.addEventListener('mousemove', function (e) {
      var opt = e.target.closest && e.target.closest(SEL.option);
      if (!opt) return;
      var opts = self.options();
      var idx = opts.indexOf(opt);
      if (idx > -1 && idx !== self.activeIndex) self.setActive(idx);
    });
  };

  PHSearch.prototype.getQuery = function () {
    return (this.input.value || '').trim();
  };

  PHSearch.prototype.onInput = function () {
    var q = this.getQuery();
    this.toggleClear(q.length > 0);

    if (q.length < this.minLength) {
      this.abort();
      this.reset();
      return;
    }
    this.search(q);
  };

  PHSearch.prototype.search = function (q) {
    var self = this;
    var key = q.toLowerCase();

    if (this.cache[key] != null) {
      this.inject(this.cache[key]);
      return;
    }

    this.toggleLoading(true);
    this.abort();

    var ac = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    this.controller = ac;

    var url =
      suggestBase() +
      '?q=' + encodeURIComponent(q) +
      '&resources[options][unavailable_products]=last' +
      '&section_id=' + SECTION_ID;

    fetch(url, ac ? { signal: ac.signal } : undefined)
      .then(function (res) {
        if (!res.ok) throw new Error('predictive-search ' + res.status);
        return res.text();
      })
      .then(function (text) {
        // The query may have changed while the request was in flight.
        if (self.getQuery().toLowerCase() !== key) return;
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var section = doc.getElementById('shopify-section-' + SECTION_ID);
        var markup = section ? section.innerHTML : text;
        self.cache[key] = markup;
        self.inject(markup);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        console.warn('[PHPredictiveSearch]', err);
        self.reset();
      })
      .then(function () {
        if (self.getQuery().toLowerCase() === key) self.toggleLoading(false);
      });
  };

  PHSearch.prototype.inject = function (markup) {
    this.results.innerHTML = markup;
    this.activeIndex = -1;
    this.toggleLoading(false);
    var hasContent = this.options().length > 0 || !!qs('[data-ph-search-empty]', this.results);
    this.setResultsState(hasContent);
    this.input.setAttribute('aria-expanded', hasContent ? 'true' : 'false');
  };

  PHSearch.prototype.reset = function () {
    this.results.innerHTML = '';
    this.activeIndex = -1;
    this.toggleLoading(false);
    this.setResultsState(false);
    this.input.setAttribute('aria-expanded', 'false');
  };

  PHSearch.prototype.setResultsState = function (on) {
    var panel = this.root;
    if (panel && panel.classList) panel.classList.toggle('has-results', !!on);
  };

  PHSearch.prototype.options = function () {
    return qsa(SEL.option, this.results);
  };

  PHSearch.prototype.setActive = function (idx) {
    var opts = this.options();
    if (!opts.length) { this.activeIndex = -1; return; }

    if (idx < 0) idx = opts.length - 1;
    if (idx > opts.length - 1) idx = 0;

    opts.forEach(function (o, i) {
      var on = i === idx;
      o.classList.toggle('is-active', on);
      o.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    this.activeIndex = idx;
    var current = opts[idx];
    if (current && current.scrollIntoView) {
      current.scrollIntoView({ block: 'nearest' });
    }
    var url = current && current.getAttribute('data-ph-search-url');
    if (url) this.input.setAttribute('aria-activedescendant', '');
  };

  PHSearch.prototype.onKeydown = function (e) {
    switch (e.key) {
      case 'ArrowDown':
        if (!this.options().length) return;
        e.preventDefault();
        this.setActive(this.activeIndex + 1);
        break;
      case 'ArrowUp':
        if (!this.options().length) return;
        e.preventDefault();
        this.setActive(this.activeIndex - 1);
        break;
      case 'Enter': {
        var opts = this.options();
        if (this.activeIndex > -1 && opts[this.activeIndex]) {
          var url = opts[this.activeIndex].getAttribute('data-ph-search-url');
          if (url) { e.preventDefault(); window.location.href = url; }
        }
        // else: let the form submit naturally to the full search page.
        break;
      }
      case 'Escape':
        e.preventDefault();
        if (this.getQuery()) {
          this.clearQuery();
        } else {
          closePopupFor(this.popup);
        }
        break;
      default:
        break;
    }
  };

  PHSearch.prototype.onSubmit = function (e) {
    var opts = this.options();
    // If an option is highlighted, follow it instead of submitting.
    if (this.activeIndex > -1 && opts[this.activeIndex]) {
      var url = opts[this.activeIndex].getAttribute('data-ph-search-url');
      if (url) { e.preventDefault(); window.location.href = url; return; }
    }
    if (!this.getQuery().length) { e.preventDefault(); }
  };

  PHSearch.prototype.clearQuery = function () {
    this.input.value = '';
    this.toggleClear(false);
    this.abort();
    this.reset();
    this.input.focus();
  };

  PHSearch.prototype.toggleLoading = function (on) {
    if (this.loadingEl) {
      this.loadingEl.classList.toggle('is-active', !!on);
      this.loadingEl.setAttribute('aria-hidden', on ? 'false' : 'true');
    }
  };

  PHSearch.prototype.toggleClear = function (on) {
    if (this.clearEl) this.clearEl.hidden = !on;
  };

  PHSearch.prototype.abort = function () {
    if (this.controller) {
      try { this.controller.abort(); } catch (err) { /* noop */ }
      this.controller = null;
    }
  };

  PHSearch.prototype.onOpen = function () {
    var self = this;
    setTimeout(function () { self.input.focus(); }, 60);
  };

  /* ---------- popup open / close (idempotent with header controller) ---------- */
  function openPopup(popup, trigger) {
    if (!popup) return;
    popup.classList.add('is-open');
    popup.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('ph-scroll-lock');
    document.documentElement.style.overflow = 'hidden';
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    var rootEl = qs(SEL.root, popup);
    if (rootEl && rootEl._phSearch) rootEl._phSearch.onOpen();
  }

  function closePopupFor(popup) {
    if (!popup) return;
    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.ph-search-popup.is-open, .ph-menu-drawer.is-open')) {
      document.documentElement.classList.remove('ph-scroll-lock');
      document.documentElement.style.overflow = '';
    }
    qsa('[data-ph-search-open][aria-expanded="true"]').forEach(function (b) {
      b.setAttribute('aria-expanded', 'false');
    });
  }

  /* ---------- delegated open/close (covers triggers outside the popup) ---------- */
  function onDocClick(e) {
    var openTrigger = e.target.closest && e.target.closest(SEL.open);
    if (openTrigger) {
      var id = openTrigger.getAttribute('aria-controls');
      var popup = id ? document.getElementById(id) : qs(SEL.popup);
      if (popup) { e.preventDefault(); openPopup(popup, openTrigger); }
      return;
    }
    var closeTrigger = e.target.closest && e.target.closest(SEL.close);
    if (closeTrigger) {
      e.preventDefault();
      closePopupFor(closeTrigger.closest(SEL.popup));
      return;
    }
    // Clicking a popular-search chip with a captured query pre-fills the input.
    var chip = e.target.closest && e.target.closest('[data-ph-search-query]');
    if (chip && chip.closest(SEL.popup)) {
      var rootEl = qs(SEL.root, chip.closest(SEL.popup));
      var ctrl = rootEl && rootEl._phSearch;
      if (ctrl && ctrl.predictive) {
        e.preventDefault();
        ctrl.input.value = chip.getAttribute('data-ph-search-query');
        ctrl.toggleClear(true);
        ctrl.input.focus();
        ctrl.onInput();
      }
    }
  }

  function onDocKeydown(e) {
    if (e.key === 'Escape') {
      var open = qs('.ph-search-popup.is-open');
      if (open && !qs(SEL.root + ':focus-within', open)) closePopupFor(open);
    }
  }

  /* ---------- init / re-init ---------- */
  function init(scope) {
    qsa(SEL.root, scope || document).forEach(function (rootEl) {
      if (rootEl._phSearch) return;
      rootEl._phSearch = new PHSearch(rootEl);
    });
  }

  function bindGlobal() {
    if (document._phSearchBound) return;
    document._phSearchBound = true;
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onDocKeydown);
  }

  window.PHPredictiveSearch = {
    _installed: true,
    init: init,
    open: function (popup, trigger) { openPopup(popup, trigger); },
    close: closePopupFor
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bindGlobal(); init(); });
  } else {
    bindGlobal();
    init();
  }

  // Re-scan in the theme editor when sections load/reload.
  document.addEventListener('shopify:section:load', function (e) {
    init(e && e.target ? e.target : document);
  });
})();
